import { Box, Button, IconButton, Paper, Popover, TextField, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/nb';
import { useState } from 'react';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
const SELECTED_COLOR = '#002655';

/**
 * One month as "august 2026": clicking the field opens a year with its twelve months, the arrows
 * change the year, one click on a month selects it. Months after maxDate can't be selected.
 */
export default function MonthPicker({ value, onChange, maxDate }: {
  value: Dayjs; onChange: (month: Dayjs) => void; maxDate?: Dayjs;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [year, setYear] = useState(value.year());

  const open = (element: HTMLElement) => {
    setYear(value.year());
    setAnchor(element);
  };
  const isAfterMax = (y: number, m: number) => maxDate != null && dayjs(new Date(y, m, 1)).isAfter(maxDate, 'month');

  return (
    <>
      <TextField
        size="small"
        fullWidth
        value={value.locale('nb').format('MMMM YYYY')}
        onClick={(e) => open(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            open(e.currentTarget);
          }
        }}
        slotProps={{ htmlInput: { readOnly: true, 'aria-haspopup': 'dialog', style: { cursor: 'pointer' } } }}
        sx={{ '& .MuiOutlinedInput-root': { cursor: 'pointer', height: 34, fontSize: '0.85rem' } }}
      />
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' } } }}
      >
        <Paper sx={{ p: 1.25, width: 252 }} role="dialog" aria-label="Velg måned">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <IconButton size="small" aria-label="Forrige år" onClick={() => setYear(year - 1)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{year}</Typography>
            <IconButton size="small" aria-label="Neste år" onClick={() => setYear(year + 1)} disabled={isAfterMax(year + 1, 0)}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
            {MONTHS.map((name, m) => {
              const selected = value.year() === year && value.month() === m;
              return (
                <Button
                  key={name}
                  variant={selected ? 'contained' : 'outlined'}
                  disabled={isAfterMax(year, m)}
                  aria-pressed={selected}
                  onClick={() => {
                    onChange(dayjs(new Date(year, m, 1)));
                    setAnchor(null);
                  }}
                  sx={{
                    minWidth: 0, height: 32, borderRadius: 1.5, textTransform: 'none', fontSize: '0.85rem', fontWeight: selected ? 600 : 400,
                    ...(selected
                      ? { backgroundColor: SELECTED_COLOR, '&:hover': { backgroundColor: SELECTED_COLOR } }
                      : { color: 'text.primary', borderColor: 'divider', '&:hover': { borderColor: 'text.secondary', backgroundColor: 'action.hover' } }),
                  }}
                >
                  {name}
                </Button>
              );
            })}
          </Box>
        </Paper>
      </Popover>
    </>
  );
}
